import { IconFileTypePdf, IconLoader2, IconPalette, IconScribble, IconTriangleSquareCircle } from "@tabler/icons-react";
import { Button, Card, Drawer, Dropdown, Empty, Flex, Form, Image, Input, Select, Skeleton, Spin, Tooltip, message } from "antd";
import Meta from "antd/es/card/Meta";
import Search from "antd/es/input/Search";
import { ImageIcon, Type } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useRecoilValue } from "recoil";
import TemplateApi from "../../api/TemplateApi";
import UserApi from "../../api/UserApi";
import RenderApi from "../../api/V1Api";
import { env } from "../../env/env";
import { CategoryDto, FolderDto, Layer, RenderDto, TemplateDto } from "../../generated/edithor-client";
import { userState } from "../../state/atoms";
import { download } from "../../util/utils";
import TemplateCard from "../TemplateCard";
import DateTimeStamp from "../util/DateTimeStamp";
import CategoryApi from "../../api/CategoryApi";
import RenderCard from "../RenderCard";
import { Helmet } from "react-helmet";
const { Option } = Select;

export default function UserRendersPage() {
  const navigate = useNavigate();
  const [messageApi, contextHolder] = message.useMessage();
  const user = useRecoilValue(userState);
  const [loading, setLoading] = useState(true);
  const [loadingRenders, setLoadingRenders] = useState(false);
  const [loadingLayers, setLoadingLayers] = useState(false);
  const [rendersDrawerOpen, setRendersDrawerOpen] = useState(false);
  const [layersDrawerOpen, setLayersDrawerOpen] = useState(false);
  const [configMasterTemplateDrawerOpen, setConfigMasterTemplateDrawerOpen] = useState(false);

  const [currentPage, setCurrentPage] = useState(0);
  const [rendersPerPage] = useState(25);
  const [loadingMore, setLoadingMore] = useState(false);
  const loaderRef = useRef(null);

  const [renders, setRenders] = useState([] as RenderDto[]);
  const [filteredRenders, setFilteredRenders] = useState([] as RenderDto[]);
  // const [selectedTemplate, setSelectedTemplate] = useState({} as TemplateDto);

  const [hasMoreRenders, setHasMoreRenders] = useState(true);

  // Adjusted function to fetch renders with pagination
  const fetchRenders = () => {
    if (user.id && hasMoreRenders) {
      setLoadingMore(true);
      UserApi.getUserRenders(user.id!, currentPage, rendersPerPage).then(resp => {
        if (resp.data.length === 0) {
          setHasMoreRenders(false);
        }

        if (currentPage === 0) {
          setRenders(resp.data);
        } else {
          setRenders(prevRenders => [...prevRenders, ...resp.data]); // Append for subsequent pages
        }
        setFilteredRenders(prevFilteredRenders => [...prevFilteredRenders, ...resp.data]);
        setLoading(false);
        setLoadingMore(false);
        if (resp.data.length < rendersPerPage) {
          // No more renders to load, handle accordingly
        }
      }).catch((error) => {
        // Handle error
        console.error("Failed to fetch renders:", error);
        setLoading(false);
        setLoadingMore(false);
      });
    }
  };


  useEffect(() => {
    fetchRenders();
  }, [user, currentPage]);

  // Scroll listener to trigger loading more renders
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setCurrentPage((prevPage) => prevPage + 1);
          }
        });
      },
      { threshold: 0.1 } // Lower threshold for testing
    );

    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }

    return () => {
      if (loaderRef.current) {
        observer.unobserve(loaderRef.current);
      }
    };
  });


  // useEffect(() => {
  //   const loadImages = async () => {
  //     const tempThumbs = {} as any;
  //     await Promise.all(
  //       templates.map(async (template, index) => {
  //         const thumbnailUrl = `${env.s3Path}public/thumbnail/${template.id}.webp?bust=${(new Date().getTime())}`;
  //         const newThumbnailUrl = `${env.apiPath}/templates/thumbnail/${template.id}`;

  //         try {
  //           const response = await fetch(thumbnailUrl);

  //           if (response.ok) {
  //             const headers = {} as any;
  //             response.headers.forEach((value, name) => headers[name] = value);

  //             if (new Date(template.updatedAt!) > new Date(headers["last-modified"])) {
  //               tempThumbs[template.id!] = newThumbnailUrl;
  //             } else {
  //               tempThumbs[template.id!] = URL.createObjectURL(await response.blob());
  //             }

  //           } else {
  //             tempThumbs[template.id!] = newThumbnailUrl;
  //           }
  //         } catch (error: any) {
  //           tempThumbs[template.id!] = newThumbnailUrl;
  //         }
  //       })
  //     );
  //     setThumbnails(tempThumbs);
  //   };

  //   loadImages();
  // }, [templates]);


  function copyId(template: TemplateDto) {
    navigator.clipboard.writeText(template.id!).then(() => {
      messageApi.success(<>You copied the template ID: <br /> <b>{template.id}</b></>);
    });
  }

  function copyToClipboard(text: string) {
    navigator.clipboard.writeText(text).then(() => {
      messageApi.success(<>Copied: <br /> <b>{text}</b></>);
    });
  }

  function downloadRender(render: RenderDto) {
    messageApi.open({
      key: 'downloading',
      type: 'loading',
      content: <>Rendering <b>{render.id}</b>... <br /></>,
      duration: 0,
    });

    // RenderApi.download(template.id!, user.apiKey!).then(resp => {
    //   download(`${env.s3Path}${resp.data.url}`, template.name, () => {
    //     messageApi.open({
    //       key: 'downloading',
    //       type: 'success',
    //       content: <>Done!</>,
    //       duration: 3,
    //     });
    //   });
    // });
  }

  // function remove(template: TemplateDto) {
  //   setTemplates(prevTemplates => prevTemplates.filter(t => t.id !== template.id));
  //   messageApi.info(`Template ${template.name} removed.`)

  //   TemplateApi.remove(template.id!).then(resp => { })
  // }

  function onSearch(value: any) {
    const filtered = renders.filter(render => {
      return render.templateName!.toLowerCase().includes(value.toLowerCase())
    });
    setFilteredRenders(filtered);
  }

  return (
    <div>
      <Helmet>
        <title>Renders | Templated</title>
      </Helmet>

      {
        loading ? (
          <div className="w-full">
            <Skeleton.Button active style={{ width: 228, height: 40, marginRight: 10 }} />
            <Skeleton.Button active style={{ width: 244, height: 40 }} />
            <Skeleton active paragraph={{ rows: 4 }} className="mt-6" />
          </div>
        ) : (
          <div className="w-full">
            {contextHolder}

            <div className="flex w-full flex-wrap justify-between mb-6 gap-4">
              <div className="text-xl flex items-center">
                {
                  renders.length > 0
                    ? "Here is the list of all your renders"
                    : <p>
                      You don't have renders yet. <br /><br />
                      Create your first template <button className="underline cursor-pointer" onClick={() => navigate('/editor')}>here</button> to start generating your images and PDFs.
                    </p>
                }
                {/* You have {renders.length} renders */}
              </div>
              {/* <div className="flex gap-4 flex-wrap">
                <Dropdown
                  trigger={["click"]}
                  placement="bottomLeft"
                  overlayClassName="border rounded"
                  menu={{
                    items: [
                      { key: '1', className: "!p-0", label: <div className="!p-3 !px-4 !text-lg" onClick={() => navigate('/editor')}>Create a blank template</div> },
                      { key: '2', className: "!p-0", label: <a className="block !p-3 !px-4 !text-lg" target="_blank" href={`${env.sitePath}/templates/`}>Customize from our Gallery</a> },
                    ]
                  }}>
                  <Button type="primary" icon={<IconScribble />} className="flex !py-6 md:!py-5 !text-xl md:!text-lg w-full md:w-auto items-center justify-center">
                    Create a template
                  </Button>
                </Dropdown>
              </div> */}

              <Search placeholder="Search your renders..." size="large" allowClear onSearch={onSearch} className="w-full md:w-80" />
            </div>

            {/* <Folders folders={folders} setFolders={setFolders} user={user} /> */}

            {/* {!renders.length && <p>You don't have renders yet. <br/><br/> Create your first template <button className="underline cursor-pointer" onClick={() => navigate('/editor')}>here</button> to start automating your images and PDFs.</p>} */}

            {/* <div className="flex gap-6 flex-wrap"> */}
            <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 3xl:grid-cols-6 4xl:grid-cols-7 5xl:grid-cols-8">
              {filteredRenders.map((render, index) => (
                <RenderCard key={render.id} render={render} user={user} loading={loading} copyId={copyId}
                // remove={remove}
                // downloadTemplate={downloadTemplate}
                />
              ))}
            </div>


            {loadingMore
              ? <div className="flex align-middle justify-between gap-2 fixed bottom-3 mx-auto bg-primary text-white px-4 py-2 rounded left-1/2">
                <IconLoader2 className="animate-spin" /> Loading more...
              </div>
              : <div ref={loaderRef} style={{ height: '20px' }}></div>
            }

            {/* <Drawer
              title={
                <div className="flex flex-col items-end text-right">
                  {selectedTemplate.name}
                  <div>{loadingRenders ? <Spin /> : <span className="text-gray-700 font-normal text-sm">({renders.length} renders)</span>}</div>
                </div>
              }
              placement="right"
              size="large"
              onClose={() => { setRendersDrawerOpen(false); setRenders([]); }}
              open={rendersDrawerOpen}
            >
              {!renders.length && !loadingRenders && <Empty />}
              {loadingRenders && <Skeleton />}

              <Flex gap={24} wrap="wrap">
                {renders.map((render, index) => (
                  <Card className={`render-card w-52 border ${loading ? 'loading' : ''}`}
                    cover={
                      render.format === 'pdf' ?
                        <a href={env.s3Path + render.url} target="_blank" rel="noreferrer">
                          <IconFileTypePdf className="w-full h-auto p-14 cursor-pointer" />
                        </a>
                        :
                        <Image
                          // width={250}
                          // height={150}
                          alt={render.id}
                          preview={true}
                          src={env.s3Path + render.url}
                          fallback={
                            "data:image/png;base64,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"
                          }
                          placeholder={
                            <Skeleton.Image style={{ width: 200, height: 200 }} active />
                          }
                        />
                    }>
                    <Meta
                      title={<DateTimeStamp date={render.createdAt!} />}
                      description={
                        <span>
                          {render.width} x {render.height} px
                        </span>
                      }
                    />
                  </Card>
                ))}
              </Flex>
            </Drawer>

            <Drawer
              title={
                <div className="flex flex-col items-end text-right">
                  {selectedTemplate.name}
                  <div className="">{loadingLayers ? <Spin /> : <span className="text-gray-700 font-normal text-sm">({layers.length} layers)</span>}</div>
                </div>
              }
              placement="right"
              size="default"
              onClose={() => { setLayersDrawerOpen(false); setLayers([]); }}
              open={layersDrawerOpen}
            >
              {!layers.length && !loadingLayers && <Empty />}
              {loadingLayers && <Skeleton />}

              <div className="flex flex-col items-end">
                {layers.map((layer) => (
                  <Tooltip title={`${layer.type!} layer`} placement="right">
                    <div className="w-fit px-4 py-1 border border-dashed rounded flex items-center text-lg cursor-pointer mb-2 bg-gray-100 hover:bg-gray-300" onClick={() => { copyToClipboard(layer.layer!) }}>
                      <span>{layer.layer}</span>
                      {layer.type === "text" && <Type className="ml-2" />}
                      {layer.type === "image" && <ImageIcon className="ml-2" />}
                      {layer.type === "shape" && <IconTriangleSquareCircle className="ml-2" />}
                    </div>
                  </Tooltip>
                ))}
              </div>
            </Drawer> */}
          </div>
        )
      }
    </div>
  )
}
